import axios from "axios";
import { useState, useEffect, useMemo, useRef } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import CountryData from "json/countries.json";
import useClickOutSideComponent from "../../../hooks/useClickOutsideComponent";

interface Country {
  name: string;
  code: string;
  flag: string;
  dialCode: string;
}

const defaultCountry: Country = {
  name: "Canada",
  code: "CA",
  flag: "https://flagcdn.com/ca.svg",
  dialCode: "+1",
};

interface PhoneData {
  country: Country | null;
  phoneNumber: string;
}

interface Props {
  onPhoneChange: (data: PhoneData) => void;
  value?: {
    countryCode: string;
    phoneNumber: string;
  };
  userCountry?: string;
  error?: string;
  labelClassName?: string;
  labelText?: string;
  isRequired?: boolean;
  lastUserCountry?: string;
  onLastUserCountryChange?: (country: string) => void;
}

const PhoneInput: React.FC<Props> = ({
  onPhoneChange,
  value,
  userCountry,
  error,
  labelClassName,
  labelText,
  isRequired,
  lastUserCountry,
  onLastUserCountryChange,
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [selectedCountry, setSelectedCountry] =
    useState<Country>(defaultCountry);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const containerRef = useRef<HTMLDivElement | null>(null);
  useClickOutSideComponent(containerRef, () => setShowDropdown(false));

  useEffect(() => {
    setCountries(CountryData);
  }, []);

  useEffect(() => {
    if (!countries.length || !value?.countryCode) return;

    const country = countries.find((c) => c.dialCode === value.countryCode);
    if (country) {
      setSelectedCountry(country);
    }
  }, [countries, value?.countryCode]);

  useEffect(() => {
    if (!countries.length || !userCountry) return;

    const userCountryMatch = countries.find(
      (c) =>
        c.name.toLowerCase() === userCountry.toLowerCase() ||
        c.code.toLowerCase() === userCountry.toLowerCase()
    );

    if (!userCountryMatch) return;

    if (lastUserCountry !== undefined && userCountry !== lastUserCountry) {
      if (!value?.phoneNumber || value.phoneNumber.trim() === "") {
        setSelectedCountry(userCountryMatch);
        onPhoneChange({ country: userCountryMatch, phoneNumber: "" });
      }
      onLastUserCountryChange?.(userCountry);
    } else if (lastUserCountry === undefined) {
      if (
        !value?.countryCode ||
        value.countryCode === userCountryMatch.dialCode
      ) {
        setSelectedCountry(userCountryMatch);
        onPhoneChange({
          country: userCountryMatch,
          phoneNumber: value?.phoneNumber || "",
        });
      }
      onLastUserCountryChange?.(userCountry);
    }
  }, [countries, userCountry, lastUserCountry]);

  const handleSelectCountry = (country: Country) => {
    setSelectedCountry(country);
    onPhoneChange({
      country: country,
      phoneNumber: value ? value.phoneNumber : "",
    });
    setShowDropdown(false);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onPhoneChange({
      country: selectedCountry,
      phoneNumber: e.target.value,
    });
  };

  const countriesFiltered = useMemo(() => {
    return countries.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [countries, search]);

  return (
    <div ref={containerRef}>
      {labelText && (
        <label
          className={` font-medium capitalize text-sm ${
            showDropdown ? "text-blueShades-4" : "text-ashShade-16"
          } ${labelClassName}`}
        >
          {labelText} {isRequired && <span className='text-ERROR'>*</span>}
        </label>
      )}
      <div
        className={`relative px-3 py-2 border flex items-center cursor-pointer rounded-md ${
          showDropdown
            ? "border-[1.5px] border-blueShades-6 outline outline-[3px] outline-[#D8E9F080]"
            : error
              ? "border-[1.5px] border-ERROR outline outline-[3px] outline-[#FFE5E5]"
              : "border-blueShades-2"
        } `}
      >
        <div
          className=' border-r border-r-ashShade-3 pr-2'
          onClick={() => setShowDropdown((prev) => !prev)}
        >
          <div className='flex'>
            <div>
              <img
                src={selectedCountry?.flag}
                alt='flag'
                className='w-6 h-4 mr-1 rounded-[4px] '
              />
            </div>
            <div>
              <IoIosArrowDown className='text-base text-ashShade-2' />
            </div>
          </div>
        </div>
        {showDropdown && (
          <div className='absolute top-10 bg-white border border-pbg  w-40 max-h-60 overflow-y-auto z-10 shadow-lg rounded-md'>
            <div className='m-2 px-2 flex bg-pbg items-center rounded-md sticky top-0 right-0 left-0'>
              <input
                type='text'
                placeholder='Search'
                className='w-full p-2 !border-transparent !border-none outline-none bg-transparent'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span>
                <IoSearchOutline className='text-xl' />
              </span>
            </div>

            {countriesFiltered.map((country) => (
              <div
                key={country.code}
                className='p-2 flex items-center hover:bg-bbg cursor-pointer font-SatoshiMedium text-sm text-bblack-1'
                onClick={() => handleSelectCountry(country)}
              >
                <img
                  src={country.flag}
                  alt='flag'
                  className='w-6 h-4 mr-2 rounded-[4px]'
                />
                {country.name}
              </div>
            ))}
          </div>
        )}
        <div className='pl-2 text-sm font-medium text-bblack-1'>
          {selectedCountry?.dialCode}
        </div>
        <input
          type='text'
          placeholder='81 xxxx xxxx'
          className='ml-2 !h-full !w-[70%] !mt-0 border-none !border-l !border-y-0 !border-r-0 !rounded-l-none focus:outline-none !text-sm'
          value={value ? value.phoneNumber : ""}
          onChange={handlePhoneChange}
        />
      </div>
      <span
        className={`text-ERROR text-[15px] ${error ? "visible" : "invisible"}`}
      >
        {error || "Placeholder"}
      </span>
    </div>
  );
};

export default PhoneInput;
